import Phaser from 'phaser';
import { useUIStore } from '@/state/uiStore';
import { useAuthStore } from '@/state/authStore';
import { TEX } from '@/data/assetManifest';

// LoginScene: cinematic MU Online style welcome screen.
// Layers (back -> front):
//   1. Cinematic key-art background (Lorencia gate at night) — cover-fit, slow ken-burns zoom
//   2. Floating ember particles drifting upward (atmospheric)
//   3. Vignette gradient (focus toward center where the form sits)
//   4. Warrior hero on the LEFT side, Wizard hero on the RIGHT side — gentle idle float
//   5. Game logo "HERO WORLD" centered near the top — subtle glow pulse
//
// The actual login form (LoginPanel) is rendered as a React HUD overlay in HUD.tsx.
export class LoginScene extends Phaser.Scene {
  private unsub?: () => void;
  private bg?: Phaser.GameObjects.Image;
  private logo?: Phaser.GameObjects.Image;
  private warrior?: Phaser.GameObjects.Image;
  private wizard?: Phaser.GameObjects.Image;
  private vignette?: Phaser.GameObjects.Graphics;
  private emberTimer?: Phaser.Time.TimerEvent;

  constructor() { super('Login'); }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;
    const CX = W / 2;

    // Detect compact / portrait physical viewport so we can hide the side
    // hero portraits when the form would otherwise overlap them on phones.
    const physW = typeof window !== 'undefined' ? window.innerWidth : W;
    const physH = typeof window !== 'undefined' ? window.innerHeight : H;
    const isCompact = physW < 768 || physW < physH;

    // 1. Cinematic background (cover-fit) — fallback to bg_town if missing.
    const bgKey = this.textures.exists(TEX.loginBg) ? TEX.loginBg : TEX.bgTown;
    this.bg = this.add.image(CX, H / 2, bgKey).setOrigin(0.5);
    this.fitCover(this.bg, W, H);
    // Slow ken-burns zoom for cinematic feel.
    const baseScale = this.bg.scaleX;
    this.tweens.add({
      targets: this.bg,
      scaleX: baseScale * 1.06,
      scaleY: baseScale * 1.06,
      duration: 18000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // 2. Soft dark overlay for legibility behind the form.
    this.add.rectangle(CX, H / 2, W, H, 0x000000, 0.25);

    // 3. Hero characters on left/right (only if assets are present).
    // On compact / portrait viewports we skip these so the central form is unobstructed.
    if (!isCompact && this.textures.exists(TEX.loginWarrior)) {
      this.warrior = this.add.image(W * 0.18, H * 0.62, TEX.loginWarrior).setOrigin(0.5);
      const targetH = H * 0.85;
      this.warrior.setScale(targetH / this.warrior.height);
      this.warrior.setAlpha(0);
      this.tweens.add({ targets: this.warrior, alpha: 0.95, duration: 1200, ease: 'Quad.easeOut' });
      // Idle float
      this.tweens.add({
        targets: this.warrior,
        y: this.warrior.y - 8,
        duration: 2600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
    if (!isCompact && this.textures.exists(TEX.loginWizard)) {
      this.wizard = this.add.image(W * 0.82, H * 0.62, TEX.loginWizard).setOrigin(0.5);
      const targetH = H * 0.85;
      this.wizard.setScale(targetH / this.wizard.height);
      this.wizard.setAlpha(0);
      this.tweens.add({ targets: this.wizard, alpha: 0.95, duration: 1200, delay: 200, ease: 'Quad.easeOut' });
      this.tweens.add({
        targets: this.wizard,
        y: this.wizard.y - 10,
        duration: 3000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: 400,
      });
    }

    // 4. Vignette (radial darkening at edges) — drawn as graphics.
    this.vignette = this.add.graphics();
    this.drawVignette(this.vignette, W, H);

    // 5. Logo at top-center — float in + glow pulse.
    if (this.textures.exists(TEX.loginLogo)) {
      this.logo = this.add.image(CX, H * 0.16, TEX.loginLogo).setOrigin(0.5);
      const targetW = W * (isCompact ? 0.62 : 0.42);
      this.logo.setScale(targetW / this.logo.width);
      this.logo.setAlpha(0);
      this.logo.y -= 30;
      this.tweens.add({
        targets: this.logo,
        alpha: 1,
        y: H * 0.16,
        duration: 1100,
        ease: 'Quad.easeOut',
      });
      // Subtle glow pulse via scale
      const baseLogoScale = this.logo.scaleX;
      this.tweens.add({
        targets: this.logo,
        scaleX: baseLogoScale * 1.025,
        scaleY: baseLogoScale * 1.025,
        duration: 2200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else {
      this.add.text(CX, H * 0.18, 'HERO WORLD', {
        fontFamily: '"Philosopher", "Marcellus SC", serif',
        fontSize: '88px',
        color: '#ffd166',
        fontStyle: '900',
      }).setOrigin(0.5).setShadow(0, 4, '#000', 8, true, true);
    }

    // 6. Ember / spark particles drifting upward for atmosphere.
    this.startEmbers(W, H);

    // 7. Subtitle below logo.
    this.add.text(CX, H * 0.27, 'AETHERIA - HẬU DUỆ ANH HÙNG CỔ ĐẠI', {
      fontFamily: '"Philosopher", "Cinzel", serif',
      fontSize: '18px',
      color: '#f3e7c2',
      fontStyle: 'italic',
    }).setOrigin(0.5).setShadow(0, 2, '#000', 6, true, true).setAlpha(0.9);

    // Tell HUD to show the login panel (React).
    useUIStore.getState().set('menu', false);

    // Listen to auth changes — once the user logs in, jump to character select.
    const checkAuth = () => {
      if (useAuthStore.getState().accessToken) {
        this.scene.start('CharacterSelect');
      }
    };
    checkAuth();
    this.unsub = useAuthStore.subscribe(checkAuth);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsub?.();
      this.emberTimer?.remove(false);
    });

    // React to viewport resize (responsive).
    this.scale.on('resize', this.handleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.handleResize, this);
    });
  }

  private fitCover(img: Phaser.GameObjects.Image, w: number, h: number) {
    const sx = w / img.width;
    const sy = h / img.height;
    const s = Math.max(sx, sy);
    img.setScale(s);
  }

  private drawVignette(g: Phaser.GameObjects.Graphics, w: number, h: number) {
    g.clear();
    // Soft dark edges using stacked translucent rounded rects.
    const layers = 14;
    for (let i = 0; i < layers; i++) {
      const t = i / layers;
      const inset = (1 - t) * Math.min(w, h) * 0.55;
      g.fillStyle(0x000000, 0.05 * t);
      g.fillRect(0, 0, w, inset);                 // top
      g.fillRect(0, h - inset, w, inset);         // bottom
      g.fillRect(0, 0, inset, h);                 // left
      g.fillRect(w - inset, 0, inset, h);         // right
    }
  }

  private startEmbers(w: number, h: number) {
    // Lightweight ember system using small image quads — avoids texture deps.
    const spawn = () => {
      const x = Phaser.Math.Between(0, w);
      const y = h + 10;
      const size = Phaser.Math.Between(2, 4);
      const ember = this.add.rectangle(x, y, size, size, 0xffae42, 0.85);
      ember.setBlendMode(Phaser.BlendModes.ADD);
      const driftX = Phaser.Math.Between(-30, 30);
      const liftDur = Phaser.Math.Between(4500, 8500);
      this.tweens.add({
        targets: ember,
        x: x + driftX,
        y: -10,
        alpha: 0,
        duration: liftDur,
        ease: 'Sine.easeOut',
        onComplete: () => ember.destroy(),
      });
    };
    this.emberTimer = this.time.addEvent({
      delay: 220,
      loop: true,
      callback: () => {
        // Spawn 1-2 embers per tick.
        spawn();
        if (Math.random() < 0.5) spawn();
      },
    });
  }

  private handleResize = (gameSize: Phaser.Structs.Size) => {
    const W = gameSize.width;
    const H = gameSize.height;
    const CX = W / 2;
    if (this.bg) {
      this.bg.setPosition(CX, H / 2);
      this.fitCover(this.bg, W, H);
    }
    if (this.logo) {
      this.logo.setPosition(CX, H * 0.16);
      const targetW = W * 0.42;
      this.logo.setScale(targetW / this.logo.width);
    }
    if (this.warrior) {
      this.warrior.setPosition(W * 0.18, H * 0.62);
      const targetH = H * 0.85;
      this.warrior.setScale(targetH / this.warrior.height);
    }
    if (this.wizard) {
      this.wizard.setPosition(W * 0.82, H * 0.62);
      const targetH = H * 0.85;
      this.wizard.setScale(targetH / this.wizard.height);
    }
    if (this.vignette) this.drawVignette(this.vignette, W, H);
  };
}
